import {
  Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, HostListener,
  computed, signal, inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CiuFitDirective } from './ciu-fit.directive';
import { ChecklistService } from '../../core/services/checklist.service';
import { ProjectService } from '../../core/services/project.service';
import { FrameworkService } from '../../core/services/framework.service';
import { TemplateService } from '../../core/services/template.service';
import { ProblemReason, ProblemReasonService } from '../../core/services/problem-reason.service';
import { AuthService } from '../../core/services/auth.service';
import { ApiErrorMessageService } from '../../core/services/api-error-message.service';
import {
  Project, ChecklistItem, ChecklistStatus, TemplateVersion, ProjectLifecycle, ProjectFramework
} from '../../core/models';

interface ProjectEditForm {
  title: string; portfolioNumber: string; rtbs: string; framework: string; manager: string; site: string; phase: string;
  lifecycle: ProjectLifecycle; complexityCode: number;
  isConfidential: boolean; confidentialityGroup: string;
}

@Component({
  selector: 'app-project-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, CiuFitDirective],
  templateUrl: './project-detail.component.html',
  styleUrl: './project-detail.component.scss',
})
export class ProjectDetailComponent implements OnChanges {
  @Input() project!: Project;
  @Output() back = new EventEmitter<void>();
  @Output() projectUpdated = new EventEmitter<Project>();

  private checklistService = inject(ChecklistService);
  private projectService = inject(ProjectService);
  private frameworkService = inject(FrameworkService);
  private templateService = inject(TemplateService);
  private problemReasonService = inject(ProblemReasonService);
  problemReasons = signal<ProblemReason[]>([]);
  private authService = inject(AuthService);
  private apiErrorMessage = inject(ApiErrorMessageService);

  readonly phases = ['Initiation', 'OoM', 'PFS', 'FS', 'EXE', 'Démarrage', 'Clôture'];
  readonly sites = ['Grande-Baie', 'Alma', 'Arvida-AP60', 'Laterrière', 'Vaudreuil'];
  readonly lifecycles: ProjectLifecycle[] = ['Actif', 'Complété', 'Annulé'];

  frameworks = signal<ProjectFramework[]>([]);
  editFrameworks = computed(() => {
    const currentCode = this.editForm().framework.trim();
    if (!currentCode || this.frameworks().some(item => item.code.trim() === currentCode))
      return this.frameworks();

    // Un cadre désactivé demeure affiché pour éviter que le navigateur ne
    // sélectionne silencieusement la première option au moment de l'édition.
    return [{
      id: 0,
      code: currentCode,
      name: 'Cadre actuel (inactif)',
      displayOrder: -1,
      defaultStartPhase: 'Initiation'
    }, ...this.frameworks()];
  });
  checklist = signal<ChecklistItem[]>([]);
  checklistLoading = signal(false);
  checklistFilter = signal('Applicables');
  activePhase = signal('Tous');
  // Conserve les livrables modifies visibles jusqu'a un changement de filtre ou de phase.
  filterVisibilityOverrides = signal<Set<string>>(new Set());

  selectedIds = signal<Set<string>>(new Set());
  batchBusy = signal(false);
  phaseApprovalSaving = signal<Set<string>>(new Set());
  batchStatus = signal<ChecklistStatus>('Complété');
  batchDate = signal(new Date().toISOString().slice(0, 10));
  batchProblemReasonId = signal('');
  batchComment = signal('');

  detailItem = signal<ChecklistItem | null>(null);
  detailHelpItemId = signal<string | null>(null);
  collapsedPhases = signal<Set<string>>(new Set());
  ciuViewOpen = signal(false);
  saved = signal(true);
  toast = signal('');

  // Edit project
  editOpen = signal(false);
  editForm = signal<ProjectEditForm>({
    title: '', portfolioNumber: '', rtbs: '', framework: '', manager: '', site: '', phase: 'Initiation',
    lifecycle: 'Actif', complexityCode: 1, isConfidential: false, confidentialityGroup: ''
  });
  editError = signal('');
  saving = signal(false);
  rtbsChangeConfirmOpen = signal(false);
  rtbsChangeConfirmed = signal(false);

  // Archive
  archiveConfirmOpen = signal(false);
  archiveBusy = signal(false);

  // Delete
  deleteConfirmOpen = signal(false);
  deleteBusy = signal(false);

  // Follow
  followBusy = signal(false);

  // Reference change
  referenceVersions = signal<TemplateVersion[]>([]);
  referenceChangeOpen = signal(false);
  targetVersionId = signal('');
  changingReference = signal(false);

  filteredChecklist = computed(() =>
    this.checklist().filter(i => {
      const phaseOk = this.activePhase() === 'Tous' || i.phase === this.activePhase();
      const f = this.checklistFilter();
      const typeOk = f === 'Tous'
        || (f === 'Applicables' && i.isApplicable)
        || (f === 'Obligatoires' && i.requirement === 'Obligatoire')
        || (f === 'Incomplets' && !['Complété', 'Non applicable'].includes(i.status))
        || (f === 'Non applicables' && i.status === 'Non applicable');
      return phaseOk && (typeOk || this.filterVisibilityOverrides().has(i.id));
    })
  );

  checklistGroups = computed(() => {
    const visible = this.filteredChecklist();
    return this.phases.map(phase => ({
      phase,
      items: visible.filter(i => i.phase === phase),
      applicable: this.checklist().filter(i => i.phase === phase && i.isApplicable).length,
      completed: this.checklist().filter(i => i.phase === phase && i.isApplicable && i.status === 'Complété').length,
      nonApplicable: this.checklist().filter(i => i.phase === phase && !i.isApplicable).length,
    })).filter(g => g.items.length > 0);
  });

  phaseStats = computed(() =>
    this.phases.map(phase => {
      const all = this.checklist().filter(i => i.phase === phase);
      const applicable = all.filter(i => i.isApplicable);
      const completed = applicable.filter(i => i.status === 'Complété');
      const nonApplicable = all.length - applicable.length;
      return {
        phase, total: all.length, applicable: applicable.length,
        completed: completed.length, nonApplicable,
        progress: applicable.length ? Math.round(completed.length / applicable.length * 100) : 0,
        allNotApplicable: all.length > 0 && nonApplicable === all.length
      };
    })
  );

  applicableCount = computed(() => this.checklist().filter(i => i.isApplicable).length);
  completedCount = computed(() => this.checklist().filter(i => i.isApplicable && i.status === 'Complété').length);
  blockedCount = computed(() => this.checklist().filter(i => i.isApplicable && i.status === 'En problème').length);
  pendingCount = computed(() => this.checklist().filter(i => i.isApplicable && i.status !== 'Complété').length);
  mandatoryCount = computed(() => this.checklist().filter(i => i.requirement === 'Obligatoire').length);
  mandatoryCompletedCount = computed(() => this.checklist().filter(i => i.requirement === 'Obligatoire' && i.status === 'Complété').length);
  overallProgress = computed(() => {
    const applicable = this.applicableCount();
    return applicable ? Math.round(this.completedCount() / applicable * 100) : 0;
  });

  /** Vue compacte destinée aux présentations CIU : phase en cours et prochaine phase applicable. */
  ciuChecklistGroups = computed(() =>
    this.ciuPhases().map(phase => ({
      phase,
      items: this.checklist().filter(item =>
        item.phase === phase
        && (item.isApplicable || item.requirement === 'Obligatoire')
      ),
    })).filter(group => group.items.length > 0)
  );
  currentPhaseStat = computed(() => this.phaseStats().find(stat => stat.phase === this.project?.phase)
    ?? { completed: 0, applicable: 0, progress: 0 });
  selectableIds = computed(() => this.filteredChecklist().map(i => i.id));
  allSelected = computed(() => {
    const ids = this.selectableIds();
    return ids.length > 0 && ids.every(id => this.selectedIds().has(id));
  });
  selectedCount = computed(() => this.selectedIds().size);
  batchRequiresComment = computed(() =>
    this.batchStatus() === 'Non applicable'
    && this.checklist().some(item =>
      this.selectedIds().has(item.id)
      && item.requirement === 'Obligatoire'
      && !this.hasComment(item))
  );

  hasComment(item: ChecklistItem): boolean {
    return !!item.comment?.trim();
  }

  toggleDetailHelp(item: ChecklistItem): void {
    this.detailHelpItemId.update(id => id === item.id ? null : item.id);
  }

  @HostListener('document:click', ['$event'])
  closeDetailHelpOnOutsideClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('.deliverable-help')) this.detailHelpItemId.set(null);
  }

  selectDetailItem(item: ChecklistItem): void {
    const isCurrentItem = this.detailItem()?.id === item.id;
    this.detailItem.set(isCurrentItem ? null : item);
    this.detailHelpItemId.set(null);
  }

  closeDetailItem(): void {
    this.detailItem.set(null);
    this.detailHelpItemId.set(null);
  }

  // Les groupes et les éléments sont recalculés après chaque mise à jour. Des
  // identifiants stables évitent de recréer toute la liste (et de perdre la
  // position de défilement ou le focus du bouton activé).
  trackByPhase(_: number, group: { phase: string }): string {
    return group.phase;
  }

  trackByChecklistItem(_: number, item: ChecklistItem): string {
    return item.id;
  }

  isUnjustifiedNotApplicable(item: ChecklistItem): boolean {
    return item.requirement === 'Obligatoire'
      && item.status === 'Non applicable'
      && !this.hasComment(item);
  }

  ciuStatusClass(item: ChecklistItem): string {
    if (item.status === 'Complété') return 'completed';
    if (item.requirement === 'Obligatoire' && item.status === 'Non applicable') return 'mandatory-na';
    if (item.requirement === 'Obligatoire') return 'mandatory-incomplete';
    return 'optional-incomplete';
  }

  printCiuView(): void {
    const ciuView = document.querySelector('.ciu-view') as HTMLElement | null;
    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (!ciuView || !printWindow) {
      this.showToast('Impossible d’ouvrir la fenêtre d’impression.');
      return;
    }

    const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map(style => style.outerHTML)
      .join('');
    const printStyles = `<style>
      @page { size: landscape; margin: 5mm; }
      @media print {
        html, body { margin: 0; padding: 0; }
        .ciu-view { width: 172.414%; height: auto; max-height: none; margin: 0; border: 0; border-radius: 0; overflow: visible; box-shadow: none; zoom: .58; }
        .ciu-actions { display: none !important; }
        .ciu-phase { break-inside: auto !important; padding-bottom: 5px; }
        .ciu-phase h3 { margin-top: 5px; padding: 3px 6px; }
        .ciu-item { grid-template-columns: minmax(280px, 2.2fr) minmax(95px, .8fr) minmax(90px, .75fr) minmax(100px, .85fr) minmax(180px, 1.4fr); }
        .ciu-item { min-height: 17px; padding-top: 1px; padding-bottom: 1px; }
        .ciu-header { padding: 7px 10px; }
        .ciu-legend { padding: 4px 10px; }
      }
    </style>`;
    printWindow.document.open();
    printWindow.document.write(`<!doctype html><html lang="${document.documentElement.lang}"><head><base href="${document.baseURI}"><title>${document.title}</title>${styles}${printStyles}</head><body>${ciuView.outerHTML}</body></html>`);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  }

  ciuPhases(): string[] {
    const currentIndex = this.phases.indexOf(this.project?.phase ?? '');
    if (currentIndex < 0) return [];

    // Certains cadres commencent plus tard dans le cycle. Les phases sautées
    // sont entièrement N/A et ne doivent donc pas occuper la place de la
    // prochaine phase dans le résumé CIU.
    const nextApplicablePhase = this.phases
      .slice(currentIndex + 1)
      .find(phase => !this.phaseStats().find(stat => stat.phase === phase)?.allNotApplicable);

    return nextApplicablePhase
      ? [this.phases[currentIndex], nextApplicablePhase]
      : [this.phases[currentIndex]];
  }

  ciuPhaseLabel(phase: string): string {
    return phase === this.project?.phase ? 'Phase en cours' : 'Prochaine phase';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['project']?.currentValue?.id !== changes['project']?.previousValue?.id && this.project) {
      this.activePhase.set(this.project.phase);
      this.collapsePastPhases();
      this.loadChecklist();
      this.problemReasons.set([]);
      this.problemReasonService.getAll().subscribe({
        next: reasons => this.problemReasons.set(reasons),
        error: error => this.showToast(this.apiErrorMessage.message(error, 'Impossible de charger les raisons des problèmes.')),
      });
      this.frameworkService.getAll().subscribe({ next: frameworks => this.frameworks.set(frameworks) });
    }
  }

  private get actor(): string {
    return this.authService.user?.sub ?? 'local.user';
  }

  loadChecklist(): void {
    this.checklistLoading.set(true);
    this.checklist.set([]);
    this.selectedIds.set(new Set());
    this.checklistService.getByProjectId(this.project.id).subscribe({
      next: items => {
        this.checklist.set(items);
        this.collapsePastPhases();
        this.checklistLoading.set(false);
      },
      error: error => { this.checklistLoading.set(false); this.showToast(this.apiErrorMessage.message(error, 'Impossible de charger la checklist.')); }
    });
  }

  toggleSelection(item: ChecklistItem, selected: boolean): void {
    const next = new Set(this.selectedIds());
    selected ? next.add(item.id) : next.delete(item.id);
    this.selectedIds.set(next);
  }

  toggleAll(selected: boolean): void {
    const next = new Set(this.selectedIds());
    this.selectableIds().forEach(id => selected ? next.add(id) : next.delete(id));
    this.selectedIds.set(next);
  }

  setStatus(item: ChecklistItem, status: ChecklistStatus): void {
    const completedDate = status === 'Complété'
      ? (item.date ?? new Date().toISOString().slice(0, 10))
      : undefined;
    const problemDate = status === 'En problème'
      ? (item.problemDate ?? new Date().toISOString().slice(0, 10))
      : item.problemDate;
    const updated: ChecklistItem = { ...item, isApplicable: status !== 'Non applicable', status, date: completedDate, problemDate };
    this.checklist.update(list => list.map(i => i.id === item.id ? updated : i));
    this.updateFilterVisibility(updated);
    if (this.detailItem()?.id === item.id || status === 'Non applicable' || status === 'En problème') this.detailItem.set(updated);
    this.saved.set(false);

    const req = this.checklistService.buildUpdateRequest(
      item, status, completedDate ?? null, problemDate ?? null, item.problemReason ?? null, item.comment ?? null, this.actor
    );
    this.checklistService.updateItem(item.id, req).subscribe({
      next: () => { this.saved.set(true); this.showToast('Modification enregistrée'); },
      error: error => {
        this.checklist.update(list => list.map(i => i.id === item.id ? item : i));
        this.updateFilterVisibility(item);
        if (this.detailItem()?.id === item.id) this.detailItem.set(item);
        this.saved.set(true);
        this.showToast(this.apiErrorMessage.message(error, 'Impossible d’enregistrer la modification.'));
      }
    });
  }

  updateCompletionDate(item: ChecklistItem, completedDate: string): void {
    if (item.status !== 'Complété' || !completedDate || item.date === completedDate) return;

    const updated: ChecklistItem = { ...item, date: completedDate };
    this.checklist.update(list => list.map(i => i.id === item.id ? updated : i));
    if (this.detailItem()?.id === item.id) this.detailItem.set(updated);
    this.saved.set(false);

    const req = this.checklistService.buildUpdateRequest(
      item, item.status, completedDate, item.problemDate ?? null, item.problemReason ?? null, item.comment ?? null, this.actor
    );
    this.checklistService.updateItem(item.id, req).subscribe({
      next: () => { this.saved.set(true); this.showToast('Date de réalisation enregistrée'); },
      error: error => {
        this.checklist.update(list => list.map(i => i.id === item.id ? item : i));
        if (this.detailItem()?.id === item.id) this.detailItem.set(item);
        this.saved.set(true);
        this.showToast(this.apiErrorMessage.message(error, "La date de réalisation n'a pas pu être enregistrée."));
      }
    });
  }

  updateComment(item: ChecklistItem, commentValue: string): void {
    const comment = commentValue.trim();
    if ((item.comment ?? '') === comment) return;

    const updated: ChecklistItem = { ...item, comment: comment || undefined };
    this.checklist.update(list => list.map(i => i.id === item.id ? updated : i));
    if (this.detailItem()?.id === item.id) this.detailItem.set(updated);
    this.saved.set(false);

    const req = this.checklistService.buildUpdateRequest(
      item, item.status, item.date ?? null, item.problemDate ?? null, item.problemReason ?? null, comment, this.actor
    );
    this.checklistService.updateItem(item.id, req).subscribe({
      next: () => { this.saved.set(true); this.showToast('Commentaire enregistré'); },
      error: error => {
        this.checklist.update(list => list.map(i => i.id === item.id ? item : i));
        if (this.detailItem()?.id === item.id) this.detailItem.set(item);
        this.saved.set(true);
        this.showToast(this.apiErrorMessage.message(error, "Le commentaire n'a pas pu être enregistré."));
      }
    });
  }

  updateProblemDate(item: ChecklistItem, problemDate: string): void {
    if (item.status !== 'En problème' || !problemDate || item.problemDate === problemDate) return;

    const updated: ChecklistItem = { ...item, problemDate };
    this.checklist.update(list => list.map(i => i.id === item.id ? updated : i));
    if (this.detailItem()?.id === item.id) this.detailItem.set(updated);
    this.saved.set(false);

    const req = this.checklistService.buildUpdateRequest(
      item, item.status, item.date ?? null, problemDate, item.problemReason ?? null, item.comment ?? null, this.actor
    );
    this.checklistService.updateItem(item.id, req).subscribe({
      next: () => { this.saved.set(true); this.showToast('Date du problème enregistrée'); },
      error: error => {
        this.checklist.update(list => list.map(i => i.id === item.id ? item : i));
        if (this.detailItem()?.id === item.id) this.detailItem.set(item);
        this.saved.set(true);
        this.showToast(this.apiErrorMessage.message(error, "La date du problème n'a pas pu être enregistrée."));
      }
    });
  }

  updateProblemReason(item: ChecklistItem, reasonValue: string): void {
    if (reasonValue === 'legacy') return;
    const reason = this.problemReasons().find(r => r.id === Number(reasonValue));
    const problemReason = reason?.name ?? '';
    if (item.problemReasonId === reason?.id && (item.problemReason ?? '') === problemReason) return;

    const updated: ChecklistItem = { ...item, problemReasonId: reason?.id, problemReason: problemReason || undefined };
    this.checklist.update(list => list.map(i => i.id === item.id ? updated : i));
    if (this.detailItem()?.id === item.id) this.detailItem.set(updated);
    this.saved.set(false);

    const req = this.checklistService.buildUpdateRequest(
      updated, item.status, item.date ?? null, item.problemDate ?? null, problemReason, item.comment ?? null, this.actor
    );
    this.checklistService.updateItem(item.id, req).subscribe({
      next: () => { this.saved.set(true); this.showToast('Raison du problème enregistrée'); },
      error: error => {
        this.checklist.update(list => list.map(i => i.id === item.id ? item : i));
        if (this.detailItem()?.id === item.id) this.detailItem.set(item);
        this.saved.set(true);
        this.showToast(this.apiErrorMessage.message(error, "La raison du problème n'a pas pu être enregistrée."));
      }
    });
  }

  applyBatch(): void {
    const ids = [...this.selectedIds()];
    if (!ids.length) return;
    const status = this.batchStatus();
    const completedDate = status === 'Complété' ? this.batchDate() : null;
    const problemDate = status === 'En problème' ? this.batchDate() : null;
    const reason = status === 'En problème'
      ? this.problemReasons().find(r => r.id === Number(this.batchProblemReasonId())) : undefined;
    const problemReason = reason?.name ?? null;
    if (status === 'Complété' && !completedDate) { this.showToast('Choisissez une date de réalisation'); return; }
    const comment = this.batchComment().trim() || null;

    this.batchBusy.set(true);
    const req = this.checklistService.buildBatchRequest(ids, status, completedDate, problemDate, problemReason, comment, this.actor, reason?.id ?? null);
    this.checklistService.batchUpdate(this.project.id, req).subscribe({
      next: () => {
        const selected = new Set(ids);
        this.checklist.update(list => list.map(i => selected.has(i.id)
          ? { ...i, status, isApplicable: status !== 'Non applicable', date: completedDate ?? undefined,
              problemDate: problemDate ?? i.problemDate,
              problemReason: problemReason ?? i.problemReason,
              problemReasonId: reason?.id ?? i.problemReasonId,
              comment: comment ?? i.comment }
          : i));
        this.checklist().filter(item => selected.has(item.id))
          .forEach(item => this.updateFilterVisibility(item));
        this.selectedIds.set(new Set());
        this.batchComment.set('');
        this.batchProblemReasonId.set('');
        this.batchBusy.set(false);
        this.showToast(`${ids.length} livrable${ids.length > 1 ? 's' : ''} mis à jour`);
      },
      error: error => { this.batchBusy.set(false); this.showToast(this.apiErrorMessage.message(error, 'Impossible de mettre à jour les livrables sélectionnés.')); }
    });
  }

  openEdit(): void {
    this.editForm.set({
      title: this.project.title, portfolioNumber: this.project.portfolioNumber ?? '', rtbs: this.project.rtbs ?? '', framework: this.project.framework.trim(), manager: this.project.manager, site: this.project.site,
      phase: this.project.phase, lifecycle: this.project.lifecycle,
      complexityCode: this.project.complexityCode ?? 1,
      isConfidential: this.project.isConfidential,
      confidentialityGroup: this.project.confidentialityGroup ?? ''
    });
    this.editError.set('');
    this.rtbsChangeConfirmed.set(false);
    this.editOpen.set(true);
  }

  saveEdit(): void {
    const f = this.editForm();
    const title = f.title.trim();
    const manager = f.manager.trim();
    const rtbsNumber = f.rtbs.trim().toUpperCase();
    if (!title || !manager || !f.framework || !f.site || !f.phase) {
      this.editError.set('Le titre, le cadre, le responsable, l\'usine et la phase sont requis.');
      return;
    }
    if (f.isConfidential && !f.confidentialityGroup.trim()) {
      this.editError.set('Un groupe AD est requis pour un projet confidentiel.');
      return;
    }
    if (rtbsNumber && !/^[PC]-\d{7}$/.test(rtbsNumber)) {
      this.editError.set('Le numéro de projet doit respecter le format P-####### ou C-#######.');
      return;
    }
    if (this.project.rtbs && !rtbsNumber) {
      this.editError.set('Le numéro RTBS ne peut pas être retiré.');
      return;
    }
    if (this.project.rtbs && rtbsNumber !== this.project.rtbs.toUpperCase()
      && !this.rtbsChangeConfirmed()) {
      this.rtbsChangeConfirmOpen.set(true);
      return;
    }
    this.saving.set(true);
    this.projectService.update(this.project.id, {
      title, portfolioNumber: f.portfolioNumber.trim() || null, rtbsNumber: rtbsNumber || null,
      confirmRtbsNumberChange: this.rtbsChangeConfirmed(), frameworkCode: f.framework, projectManager: manager,
      siteCode: this.projectService.siteCode(f.site),
      currentPhase: f.phase,
      lifecycleStatus: this.projectService.lifecycleValue(f.lifecycle),
      complexityCode: Number(f.complexityCode),
      isConfidential: f.isConfidential,
      confidentialityGroup: f.isConfidential ? f.confidentialityGroup.trim() : null
    }).subscribe({
      next: () => {
        const frameworkChanged = f.framework !== this.project.framework;
        const updated = { ...this.project, title, rtbs: rtbsNumber || undefined, portfolioNumber: f.portfolioNumber.trim() || undefined, framework: f.framework, manager, site: f.site, phase: f.phase,
          lifecycle: f.lifecycle, complexityCode: Number(f.complexityCode),
          isConfidential: f.isConfidential,
          confidentialityGroup: f.isConfidential ? f.confidentialityGroup.trim() : undefined };
        this.projectUpdated.emit(updated);
        this.saving.set(false);
        this.editOpen.set(false);
        if (frameworkChanged) this.loadChecklist();
        this.showToast('Fiche projet enregistrée');
      },
      error: err => {
        this.saving.set(false);
        this.editError.set(this.apiErrorMessage.message(err, 'Impossible d\'enregistrer les modifications.'));
      }
    });
  }

  archive(archived = true): void {
    this.archiveBusy.set(true);
    this.projectService.archive(this.project.id, archived).subscribe({
      next: () => {
        const updated = { ...this.project, archived };
        this.projectUpdated.emit(updated);
        this.archiveBusy.set(false);
        this.archiveConfirmOpen.set(false);
        if (archived) { this.back.emit(); this.showToast('Projet archivé'); }
        else this.showToast('Projet restauré dans le portefeuille');
      },
      error: err => {
        this.archiveBusy.set(false);
        this.showToast(err.error?.message || 'Impossible de modifier l\'archivage');
      }
    });
  }

  follow(): void {
    if (this.followBusy()) return;
    const userName = this.authService.user?.sub ?? '';
    if (!userName) { this.showToast('Identifiant utilisateur introuvable'); return; }
    this.followBusy.set(true);
    const following = !this.project.followed;
    this.projectService.follow(this.project.id, userName, following).subscribe({
      next: () => {
        const updated = { ...this.project, followed: following };
        this.projectUpdated.emit(updated);
        this.followBusy.set(false);
        this.showToast(following ? 'Projet ajouté à Mes projets' : 'Projet retiré de Mes projets');
      },
      error: () => { this.followBusy.set(false); this.showToast('Impossible de modifier le suivi'); }
    });
  }

  openReferenceChange(): void {
    this.templateService.getAll().subscribe({
      next: versions => {
        this.referenceVersions.set(versions);
        this.targetVersionId.set(
          versions.find(v => v.status === 'Published' && v.id !== this.project.templateVersionId)?.id ?? ''
        );
        this.referenceChangeOpen.set(true);
      },
      error: () => this.showToast('Impossible de charger les versions du référentiel')
    });
  }

  changeReference(): void {
    const targetId = this.targetVersionId();
    if (!targetId) return;
    this.changingReference.set(true);
    this.projectService.changeTemplateVersion(this.project.id, targetId, this.actor).subscribe({
      next: result => {
        const updated = { ...this.project, templateVersionId: result.templateVersionId };
        this.projectUpdated.emit(updated);
        this.referenceChangeOpen.set(false);
        this.changingReference.set(false);
        this.loadChecklist();
        this.showToast(`Référentiel v${result.versionNumber} appliqué · ${result.preserved} conservés, ${result.added} ajoutés`);
      },
      error: err => {
        this.changingReference.set(false);
        this.showToast(err.error?.message || 'Impossible de changer le référentiel');
      }
    });
  }

  isPreviousPhaseIncomplete(phase: string): boolean {
    const currentIdx = this.phases.indexOf(this.project?.phase ?? '');
    const phaseIdx = this.phases.indexOf(phase);
    const stat = this.phaseStats().find(s => s.phase === phase);
    return currentIdx > phaseIdx && phaseIdx >= 0 && !!stat && stat.completed < stat.applicable;
  }

  isOverdue(item: ChecklistItem): boolean {
    return this.isPreviousPhaseIncomplete(item.phase)
      && !['Complété', 'Non applicable'].includes(item.status);
  }

  updatePhaseApprovalDate(phase: string, value: string): void {
    if (this.phaseApprovalSaving().has(phase)) return;

    const approvalDate = value || null;
    const existing = this.project.phaseApprovalDates[phase] ?? null;
    if (existing === approvalDate) return;

    this.phaseApprovalSaving.update(saving => new Set(saving).add(phase));
    this.projectService.updatePhaseApprovalDate(this.project.id, phase, approvalDate).subscribe({
      next: () => {
        const phaseApprovalDates = { ...this.project.phaseApprovalDates };
        if (approvalDate) phaseApprovalDates[phase] = approvalDate;
        else delete phaseApprovalDates[phase];
        this.projectUpdated.emit({ ...this.project, phaseApprovalDates });
        this.finishPhaseApprovalSave(phase);
        this.showToast(approvalDate ? 'Date d’approbation enregistrée' : 'Date d’approbation retirée');
      },
      error: error => {
        this.finishPhaseApprovalSave(phase);
        this.showToast(this.apiErrorMessage.message(error, 'Impossible d’enregistrer la date d’approbation.'));
      }
    });
  }

  deleteProject(): void {
    this.deleteBusy.set(true);
    this.projectService.delete(this.project.id).subscribe({
      next: () => {
        this.deleteBusy.set(false);
        this.deleteConfirmOpen.set(false);
        this.back.emit();
        this.showToast('Projet supprimé');
      },
      error: err => {
        this.deleteBusy.set(false);
        this.showToast(err.error?.message || 'Impossible de supprimer le projet');
      }
    });
  }

  showToast(msg: string): void {
    this.toast.set(msg);
    setTimeout(() => this.toast.set(''), 2400);
  }

  // ── Template helper methods (arrow functions not allowed in templates) ──────
  clearSelection(): void { this.selectedIds.set(new Set()); }
  setChecklistFilter(filter: string): void {
    this.checklistFilter.set(filter);
    this.filterVisibilityOverrides.set(new Set());
  }
  selectPhase(phase: string): void {
    this.activePhase.set(phase);
    this.filterVisibilityOverrides.set(new Set());
    if (phase !== 'Tous') {
      this.collapsedPhases.update(collapsed => {
        const next = new Set(collapsed);
        next.delete(phase);
        return next;
      });
    }
  }
  private collapsePastPhases(): void {
    const currentPhaseIndex = this.phases.indexOf(this.project.phase);
    this.collapsedPhases.set(new Set(currentPhaseIndex > 0
      ? this.phases.slice(0, currentPhaseIndex)
      : []));
  }
  isPhaseCollapsed(phase: string): boolean { return this.collapsedPhases().has(phase); }
  private finishPhaseApprovalSave(phase: string): void {
    this.phaseApprovalSaving.update(saving => {
      const next = new Set(saving);
      next.delete(phase);
      return next;
    });
  }
  togglePhase(phase: string): void {
    this.collapsedPhases.update(collapsed => {
      const next = new Set(collapsed);
      next.has(phase) ? next.delete(phase) : next.add(phase);
      return next;
    });
  }
  private updateFilterVisibility(item: ChecklistItem): void {
    this.filterVisibilityOverrides.update(overrides => {
      const next = new Set(overrides);
      if (this.matchesChecklistFilter(item, this.checklistFilter())) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
  }
  private matchesChecklistFilter(item: ChecklistItem, filter: string): boolean {
    return filter === 'Tous'
      || (filter === 'Applicables' && item.isApplicable)
      || (filter === 'Obligatoires' && item.requirement === 'Obligatoire')
      || (filter === 'Incomplets' && !['Complété', 'Non applicable'].includes(item.status))
      || (filter === 'Non applicables' && !item.isApplicable);
  }
  setEditTitle(v: string): void  { this.editForm.update(f => ({...f, title: v})); }
  setEditPortfolioNumber(v: string): void { this.editForm.update(f => ({...f, portfolioNumber: v})); }
  setEditRtbs(v: string): void {
    this.rtbsChangeConfirmed.set(false);
    this.editForm.update(f => ({...f, rtbs: v}));
  }
  confirmRtbsChange(): void {
    this.rtbsChangeConfirmed.set(true);
    this.rtbsChangeConfirmOpen.set(false);
    this.saveEdit();
  }
  setEditFramework(v: string): void { this.editForm.update(f => ({...f, framework: v})); }
  setEditManager(v: string): void { this.editForm.update(f => ({...f, manager: v})); }
  setEditSite(v: string): void   { this.editForm.update(f => ({...f, site: v})); }
  setEditPhase(v: string): void  { this.editForm.update(f => ({...f, phase: v})); }
  setEditLifecycle(v: string): void { this.editForm.update(f => ({...f, lifecycle: v as ProjectLifecycle})); }
  setEditComplexity(v: number): void { this.editForm.update(f => ({...f, complexityCode: v})); }
}
